import { LelyAuth } from '@hn-work/frontend-vue-shared';

/** The alarm view the app hands over to when no deep link says otherwise. */
export const NEW_APP_ALARMS_HOME = '/alarms/open';

/**
 * A link into one of the new (Angular) apps at `base`, passing the access token
 * so the cross-domain handoff stays authenticated (the new apps read
 * `?access_token=` on load, then strip it from the URL).
 */
function handoffUrl(
    base: string | undefined,
    path: string,
    query: Record<string, string | undefined>,
): string {
    const root = (base || '').replace(/\/+$/, '');
    const token = LelyAuth.getAccessToken();
    const params: Record<string, string | undefined> = {
        ...query,
        access_token: token ? String(token) : undefined,
    };

    const search = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
        .join('&');

    return search ? `${root}${path}?${search}` : `${root}${path}`;
}

/** Builds a link into the new Alarm Center for modules that have moved there. */
export function newAppUrl(
    path: string,
    query: Record<string, string | undefined> = {},
): string {
    return handoffUrl(process.env.VUE_APP_NEW_APP_URL, path, query);
}

/**
 * Builds a link into the knowledge base, which moved to its own app. Its routes
 * are this app's `/knowledge-base/…` routes without that prefix, e.g.
 * `/exos/farm/12` or `/exos/31004`.
 */
export function knowledgeBaseUrl(
    path = '/',
    query: Record<string, string | undefined> = {},
): string {
    return handoffUrl(process.env.VUE_APP_KNOWLEDGE_BASE_URL, path, query);
}

/**
 * The new app's view of a legacy alarm link. This app addressed alarms as
 * `/#/?run=<id>&notification=<id>` (the "copy link" action, the ID search, the
 * links the backend sends out) and scoped the list with `?vehicleId=`; the new
 * app calls those `run`, `alarm` and `vehicleId`, so an old link keeps opening
 * the alarm — or the vehicle's alarms — it was made for.
 */
export function newAppAlarmUrl(
    notificationId?: string,
    runId?: string,
    vehicleId?: string,
): string {
    return newAppUrl(NEW_APP_ALARMS_HOME, {
        alarm: notificationId,
        run: runId,
        vehicleId,
    });
}
